import { useEffect, useState, useCallback } from 'react';
import {
  Video,
  Inbox,
  CalendarClock,
  CalendarCheck,
  Clock,
  CheckCircle2,
  Check,
  Pencil,
  Ban,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../store/authStore';
import { useDeepLinkHighlight } from '../lib/useDeepLinkHighlight';

// FB-7 — Panel de análisis de vídeo en el dashboard WEB del entrenador.
// Reusa el flujo y las acciones que ya existen en VideoAnalysisPage (admin) y en
// la app del entrenador (PJ-4), y añade la fase 1 "marcar vídeo recibido"
// (mig 143, columna video_received_at). RLS: el SELECT/UPDATE de la tabla ya
// limita al entrenador asignado a la reserva; además filtramos por trainer_id.

export type VABStatus = 'pending' | 'scheduled' | 'completed' | 'cancelled';

export interface VideoBooking {
  id: string;
  user_id: string;
  status: VABStatus;
  scheduled_at: string | null;
  completed_at: string | null;
  video_received_at: string | null;
  created_at: string;
  student: { full_name: string } | null;
}

// Los 5 grupos derivados que pide FB-7. `cancelled` no se lista (no se pide).
export type VABGroupKey =
  | 'video_pending' // pending + video_received_at NULL  → "vídeo por recibir"
  | 'awaiting_feedback' // pending + video_received_at NOT NULL → "agendar reunión de feedback"
  | 'scheduled_upcoming' // scheduled + scheduled_at >= now()
  | 'pending_complete' // scheduled + scheduled_at < now()
  | 'completed'; // completed

// Pura y exportada para test: clasifica una reserva en su grupo según el estado
// y el instante `nowMs`. Una reserva 'scheduled' sin fecha cae en
// "pendiente de completar" (no debería ocurrir, pero es el lado seguro).
export function classifyBooking(b: VideoBooking, nowMs: number): VABGroupKey | null {
  if (b.status === 'cancelled') return null;
  if (b.status === 'completed') return 'completed';
  if (b.status === 'pending') {
    return b.video_received_at ? 'awaiting_feedback' : 'video_pending';
  }
  // scheduled
  const t = b.scheduled_at ? Date.parse(b.scheduled_at) : NaN;
  return !Number.isNaN(t) && t >= nowMs ? 'scheduled_upcoming' : 'pending_complete';
}

export function groupBookings(
  bookings: VideoBooking[],
  nowMs: number
): Record<VABGroupKey, VideoBooking[]> {
  const groups: Record<VABGroupKey, VideoBooking[]> = {
    video_pending: [],
    awaiting_feedback: [],
    scheduled_upcoming: [],
    pending_complete: [],
    completed: [],
  };
  for (const b of bookings) {
    const key = classifyBooking(b, nowMs);
    if (key) groups[key].push(b);
  }
  return groups;
}

const GROUP_META: Record<
  VABGroupKey,
  { label: string; icon: typeof Inbox; tone: string; testid: string }
> = {
  video_pending: {
    label: 'Vídeo por recibir',
    icon: Inbox,
    tone: 'text-[#FF9800] border-[#FF9800]/30 bg-[#FF9800]/5',
    testid: 'grupo-video-pending',
  },
  awaiting_feedback: {
    label: 'Pendiente de feedback · agendar reunión',
    icon: CalendarClock,
    tone: 'text-[#2196F3] border-[#2196F3]/30 bg-[#2196F3]/5',
    testid: 'grupo-awaiting-feedback',
  },
  scheduled_upcoming: {
    label: 'Agendado · próximo',
    icon: CalendarCheck,
    tone: 'text-[#4CAF50] border-[#4CAF50]/30 bg-[#4CAF50]/5',
    testid: 'grupo-scheduled-upcoming',
  },
  pending_complete: {
    label: 'Pendiente de completar',
    icon: Clock,
    tone: 'text-[#FF9800] border-[#FF9800]/30 bg-[#FF9800]/5',
    testid: 'grupo-pending-complete',
  },
  completed: {
    label: 'Realizado',
    icon: CheckCircle2,
    tone: 'text-text-secondary border-bg-border bg-bg-elevated',
    testid: 'grupo-completed',
  },
};

// Orden de presentación de los grupos (los accionables primero).
const GROUP_ORDER: VABGroupKey[] = [
  'video_pending',
  'awaiting_feedback',
  'pending_complete',
  'scheduled_upcoming',
  'completed',
];

// Máximo de realizadas que se listan (el resto se cuenta pero no se pinta).
const MAX_COMPLETED = 5;

function fmt(ts: string | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function MonitorVideoAnalysisPanel() {
  const { profile } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [bookings, setBookings] = useState<VideoBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  // El panel se auto-oculta cuando no hay reservas, así que un fallo de
  // lectura lo hacía DESAPARECER del dashboard del entrenador: ni cifra, ni
  // frase, ni hueco. No hay nada que contradecir.
  const [errorCarga, setErrorCarga] = useState<string | null>(null);

  // C-3.1: al llegar desde la agenda con ?booking=<id>, localizar esa reserva
  // (fila `va-row-<id>`), hacer scroll hasta ella y resaltarla. Toda reserva
  // 'scheduled' que venga de la agenda cae en un grupo visible (sin recorte).
  const bookingParam = searchParams.get('booking');
  const highlighted = useDeepLinkHighlight(bookingParam, 'va-row-', !loading, bookings.length);

  const load = useCallback(async () => {
    if (!profile) return;
    // RLS limita a las reservas del entrenador asignado; el filtro explícito
    // por trainer_id las acota a las suyas (no las de admin). Se excluyen las
    // canceladas (no entran en ninguno de los 5 grupos).
    const { data, error: errorLectura } = await supabase
      .from('video_analysis_bookings')
      .select(
        'id, user_id, status, scheduled_at, completed_at, video_received_at, created_at, student:profiles!video_analysis_bookings_user_id_fkey(full_name)'
      )
      .eq('trainer_id', profile.id)
      .in('status', ['pending', 'scheduled', 'completed'])
      .order('created_at', { ascending: true });
    if (errorLectura) {
      setErrorCarga(errorLectura.message || 'Error desconocido');
      setBookings([]);
      setLoading(false);
      return;
    }
    setErrorCarga(null);
    setBookings((data ?? []) as unknown as VideoBooking[]);
    setLoading(false);
  }, [profile]);

  useEffect(() => {
    load();
  }, [load]);

  // Fase 1 (FB-7) — el entrenador marca que recibió el vídeo (llega por chat).
  // No cambia el estado: sigue 'pending', pero pasa a "pendiente de feedback".
  async function markVideoReceived(b: VideoBooking) {
    setBusy(b.id);
    setError('');
    const { error: e } = await supabase
      .from('video_analysis_bookings')
      .update({ video_received_at: new Date().toISOString() })
      .eq('id', b.id);
    if (e) setError(e.message);
    await load();
    setBusy(null);
  }

  // Fase 2 — agendar (o reagendar) la reunión de feedback. Mismo patrón que
  // VideoAnalysisPage (admin): prompt de fecha/hora → scheduled + scheduled_at.
  async function schedule(b: VideoBooking) {
    const base = b.scheduled_at ? new Date(b.scheduled_at) : new Date();
    const when = window.prompt(
      'Fecha y hora de la reunión de feedback (YYYY-MM-DD HH:MM):',
      base.toISOString().slice(0, 16).replace('T', ' ')
    );
    if (!when) return;
    const iso = new Date(when.replace(' ', 'T')).toISOString();
    if (Number.isNaN(Date.parse(iso))) {
      setError('Fecha no válida.');
      return;
    }
    setBusy(b.id);
    setError('');
    const { error: e } = await supabase
      .from('video_analysis_bookings')
      .update({ status: 'scheduled', scheduled_at: iso })
      .eq('id', b.id);
    if (e) setError(e.message);
    await load();
    setBusy(null);
  }

  // Realizar (fase 2 completada): RPC con decremento atómico del crédito.
  async function complete(b: VideoBooking) {
    setBusy(b.id);
    setError('');
    const { data, error: e } = await supabase.rpc('complete_video_analysis', {
      p_booking_id: b.id,
    });
    const res = data as { ok?: boolean; error?: string } | null;
    if (e) setError(e.message);
    else if (res && res.ok === false) setError(res.error ?? 'No se pudo completar');
    await load();
    setBusy(null);
  }

  async function cancelBooking(b: VideoBooking) {
    const ok = window.confirm(
      `¿Cancelar la solicitud de ${b.student?.full_name ?? 'este alumno'}? El crédito queda disponible para una nueva solicitud.`
    );
    if (!ok) return;
    setBusy(b.id);
    setError('');
    const { error: e } = await supabase
      .from('video_analysis_bookings')
      .update({ status: 'cancelled' })
      .eq('id', b.id);
    if (e) setError(e.message);
    await load();
    setBusy(null);
  }

  if (loading) return null;
  // Un fallo de lectura NO se auto-oculta: ocultarse afirma «no tienes
  // reservas de vídeo», y una reserva sin agendar esperando al entrenador es
  // justo lo que este panel existe para que no se quede esperando.
  if (errorCarga) {
    return (
      <div className="card" data-testid="monitor-video-ilegible">
        <p className="text-error text-sm font-inter">
          No se han podido cargar tus reservas de análisis de vídeo. No es que no tengas ninguna.
        </p>
        <button
          className="btn-secondary mt-3"
          onClick={load}
          data-testid="monitor-video-reintentar"
        >
          Reintentar
        </button>
      </div>
    );
  }
  // No ensuciar el dashboard de entrenadores sin análisis de vídeo.
  if (bookings.length === 0) return null;

  const now = Date.now();
  const groups = groupBookings(bookings, now);

  function renderActions(b: VideoBooking, key: VABGroupKey) {
    const disabled = busy === b.id;
    return (
      <div className="flex items-center gap-2">
        {key === 'video_pending' && (
          <button
            onClick={() => markVideoReceived(b)}
            disabled={disabled}
            data-testid={`marcar-recibido-${b.id}`}
            title="Marcar vídeo recibido"
            className="flex items-center gap-1.5 text-xs font-inter text-[#2196F3] border border-[#2196F3]/40 rounded-lg px-2.5 py-1 hover:bg-[#2196F3]/10 transition-colors"
          >
            <Inbox size={13} /> Vídeo recibido
          </button>
        )}
        {key === 'awaiting_feedback' && (
          <button
            onClick={() => schedule(b)}
            disabled={disabled}
            data-testid={`agendar-${b.id}`}
            title="Agendar reunión de feedback"
            className="flex items-center gap-1.5 text-xs font-inter text-gold-primary border border-gold-primary/40 rounded-lg px-2.5 py-1 hover:bg-gold-primary/10 transition-colors"
          >
            <CalendarClock size={13} /> Agendar feedback
          </button>
        )}
        {(key === 'scheduled_upcoming' || key === 'pending_complete') && (
          <>
            <button
              onClick={() => schedule(b)}
              disabled={disabled}
              data-testid={`editar-${b.id}`}
              title="Editar fecha/hora"
              className="text-text-secondary hover:text-gold-primary transition-colors"
            >
              <Pencil size={16} />
            </button>
            <button
              onClick={() => complete(b)}
              disabled={disabled}
              data-testid={`completar-${b.id}`}
              title="Marcar realizada (decrementa crédito)"
              className="text-text-secondary hover:text-success transition-colors"
            >
              <Check size={16} />
            </button>
          </>
        )}
        {key !== 'completed' && (
          <button
            onClick={() => cancelBooking(b)}
            disabled={disabled}
            data-testid={`cancelar-${b.id}`}
            title="Cancelar solicitud"
            className="text-text-secondary hover:text-error transition-colors"
          >
            <Ban size={16} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="video-analysis-panel">
      <h3 className="font-oswald text-lg uppercase tracking-widest text-text-secondary mb-4 flex items-center gap-2">
        <Video size={18} className="text-gold-primary" /> Análisis de vídeo
      </h3>

      {error && <p className="text-error text-sm bg-error/10 rounded-lg p-3 mb-4">{error}</p>}

      <div className="space-y-5">
        {GROUP_ORDER.map((key) => {
          const list = groups[key];
          if (list.length === 0) return null;
          const meta = GROUP_META[key];
          const Icon = meta.icon;
          const visible = key === 'completed' ? list.slice(0, MAX_COMPLETED) : list;
          return (
            <div key={key} data-testid={meta.testid}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className={meta.tone.split(' ')[0]} />
                <span className="font-inter text-xs uppercase tracking-wider text-text-secondary">
                  {meta.label}
                </span>
                <span
                  className="font-oswald text-sm text-white"
                  data-testid={`${meta.testid}-count`}
                >
                  {list.length}
                </span>
              </div>
              <div className="space-y-2">
                {visible.map((b) => (
                  <div
                    key={b.id}
                    data-testid={`va-row-${b.id}`}
                    className={`card flex items-center justify-between border transition-shadow ${meta.tone} ${
                      highlighted === b.id
                        ? 'ring-2 ring-gold-primary ring-offset-2 ring-offset-bg-primary'
                        : ''
                    }`}
                  >
                    <div>
                      <p className="font-oswald text-white text-base">
                        {b.student?.full_name ?? 'Alumno'}
                      </p>
                      <p className="font-inter text-text-secondary text-xs mt-0.5">
                        {key === 'completed'
                          ? `Realizada · ${fmt(b.completed_at)}`
                          : key === 'scheduled_upcoming' || key === 'pending_complete'
                            ? `Reunión · ${fmt(b.scheduled_at)}`
                            : key === 'awaiting_feedback'
                              ? `Vídeo recibido · ${fmt(b.video_received_at)}`
                              : `Solicitado · ${fmt(b.created_at)}`}
                      </p>
                    </div>
                    {renderActions(b, key)}
                  </div>
                ))}
              </div>
              {key === 'completed' && list.length > visible.length && (
                <p className="mt-2 font-inter text-xs text-text-secondary">
                  Mostrando {visible.length} de {list.length} realizadas.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
